// Portfolio Results with Heatmap and Modal
import React, { useState } from 'react';
import './PortfolioResults.css';

const PortfolioResults = ({ results, onBack }) => {
  const [selectedStock, setSelectedStock] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Get heatmap data sorted by performance
  const heatmapData = results.heatmap_data || [];
  const portfolioMetrics = results.portfolio_metrics || {};
  const summary = results.summary || {};

  // Color mapping for heatmap
  const getHeatmapColor = (returnPct) => {
    if (returnPct >= 20) return '#006400'; // Dark green
    if (returnPct >= 10) return '#32CD32'; // Lime green  
    if (returnPct >= 5) return '#90EE90';  // Light green
    if (returnPct >= 0) return '#E8F5E8';  // Very light green
    if (returnPct >= -5) return '#FFE8E8'; // Very light red
    if (returnPct >= -10) return '#FFB6C1'; // Light pink
    if (returnPct >= -20) return '#FF69B4'; // Hot pink
    return '#DC143C'; // Crimson red
  };

  const getTextColor = (returnPct) => {
    if (returnPct >= 5 || returnPct <= -10) return 'white';
    return '#2c3e50';
  };

  const openStockDetails = (stockData) => {
    // Find the full result data for this stock
    const fullResult = results.individual_results.find(r => r.symbol === stockData.symbol);
    setSelectedStock({ ...stockData, fullResult });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStock(null);
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="portfolio-results">
      {/* Header */}
      <div className="results-header">
        <button onClick={onBack} className="back-btn">
          ← Back to Portfolio Builder
        </button>
        <div className="header-info">
          <h1>Portfolio Backtest Results</h1>
          <div className="header-stats">
            <span>{summary.successful_stocks}/{summary.total_stocks} stocks successful</span>
            <span>•</span>
            <span>{formatNumber(summary.success_rate, 1)}% success rate</span>
          </div>
        </div>
      </div>

      {/* Portfolio Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card primary">
          <div className="card-header">Portfolio Return</div>
          <div className="card-value">
            {portfolioMetrics.portfolio_return > 0 ? '+' : ''}
            {formatNumber(portfolioMetrics.portfolio_return)}%
          </div>
          <div className="card-subtitle">Average across all stocks</div>
        </div>

        <div className="summary-card">
          <div className="card-header">Best Performer</div>
          <div className="card-value">{portfolioMetrics.best_stock?.symbol || 'N/A'}</div>
          <div className="card-subtitle">
            {portfolioMetrics.best_stock ? 
              `+${formatNumber(portfolioMetrics.best_stock.return)}%` : 'N/A'}
          </div>
        </div>

        <div className="summary-card">
          <div className="card-header">Worst Performer</div>
          <div className="card-value">{portfolioMetrics.worst_stock?.symbol || 'N/A'}</div>
          <div className="card-subtitle">
            {portfolioMetrics.worst_stock ? 
              `${formatNumber(portfolioMetrics.worst_stock.return)}%` : 'N/A'}
          </div>
        </div>

        <div className="summary-card">
          <div className="card-header">Total Trades</div>
          <div className="card-value">{portfolioMetrics.total_trades || 0}</div>
          <div className="card-subtitle">Across all stocks</div>
        </div>

        <div className="summary-card">
          <div className="card-header">Average Win Rate</div>
          <div className="card-value">{formatNumber(portfolioMetrics.win_rate)}%</div>
          <div className="card-subtitle">Success rate</div>
        </div>

        <div className="summary-card">
          <div className="card-header">Portfolio Volatility</div>
          <div className="card-value">{formatNumber(portfolioMetrics.volatility)}%</div>
          <div className="card-subtitle">Standard deviation</div>
        </div>
      </div>

      {/* Heatmap Section */}
      <div className="heatmap-section">
        <div className="heatmap-header">
          <h2>Performance Heatmap</h2>
          <p>Click any stock to view detailed statistics</p>
          
          {/* Color Legend */}
          <div className="heatmap-legend">
            <div className="legend-title">Return %</div>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#006400'}}></div>
                <span>≥20%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#32CD32'}}></div>
                <span>10-20%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#90EE90'}}></div>
                <span>5-10%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#E8F5E8'}}></div>
                <span>0-5%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#FFE8E8'}}></div>
                <span>0 to -5%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#FFB6C1'}}></div>
                <span>-5 to -10%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#FF69B4'}}></div>
                <span>-10 to -20%</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#DC143C'}}></div>
                <span>≤-20%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Heatmap Grid */}
        <div className="heatmap-grid">
          {heatmapData.map((stock, index) => (
            <div
              key={stock.symbol}
              className={`heatmap-cell ${stock.error ? 'error' : ''}`}
              style={{
                backgroundColor: stock.error ? '#f8f8f8' : getHeatmapColor(stock.return_pct),
                color: stock.error ? '#666' : getTextColor(stock.return_pct),
                cursor: stock.error ? 'default' : 'pointer'
              }}
              onClick={() => !stock.error && openStockDetails(stock)}
              title={stock.error ? `Error: ${stock.error}` : `Click for details`}
            >
              <div className="cell-rank">#{index + 1}</div>
              <div className="cell-symbol">{stock.symbol}</div>
              <div className="cell-return">
                {stock.error ? 'Error' : (
                  <>
                    {stock.return_pct > 0 ? '+' : ''}
                    {formatNumber(stock.return_pct)}%
                  </>
                )}
              </div>
              <div className="cell-trades">
                {stock.error ? '' : `${stock.trades || 0} trades`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Portfolio Metrics */}
      <div className="additional-metrics">
        <h3>Portfolio Analytics</h3>
        <div className="metrics-grid">
          <div className="metric-item">
            <span className="metric-label">Return Range:</span>
            <span className="metric-value">
              {formatNumber(portfolioMetrics.return_range?.min)}% to {formatNumber(portfolioMetrics.return_range?.max)}%
            </span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Return Spread:</span>
            <span className="metric-value">{formatNumber(portfolioMetrics.return_range?.spread)}%</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Risk-Adjusted Return:</span>
            <span className="metric-value">{formatNumber(portfolioMetrics.risk_adjusted_return)}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Average Sharpe Ratio:</span>
            <span className="metric-value">{formatNumber(portfolioMetrics.sharpe_ratio)}</span>
          </div>
        </div>
      </div>

      {/* Stock Details Modal */}
      {showModal && selectedStock && (
        <StockDetailsModal 
          stock={selectedStock} 
          onClose={closeModal}
        />
      )}
    </div>
  );
};

// Stock Details Modal Component
const StockDetailsModal = ({ stock, onClose }) => {
  const result = stock.fullResult?.result;
  
  if (!result) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{stock.symbol} Details</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>No detailed data available for this stock.</p>
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{stock.symbol} - Detailed Statistics</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Key Metrics */}
          <div className="modal-section">
            <h3>Performance Overview</h3>
            <div className="modal-metrics">
              <div className="modal-metric">
                <span className="metric-label">Total Return:</span>
                <span className={`metric-value ${result.total_return >= 0 ? 'positive' : 'negative'}`}>
                  {result.total_return > 0 ? '+' : ''}{formatNumber(result.total_return)}%
                </span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Total Trades:</span>
                <span className="metric-value">{result.total_trades}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Win Rate:</span>
                <span className="metric-value">{formatNumber(result.win_rate)}%</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Profit Factor:</span>
                <span className="metric-value">{formatNumber(result.profit_factor)}</span>
              </div>
            </div>
          </div>

          {/* Risk Metrics */}
          <div className="modal-section">
            <h3>Risk Analysis</h3>
            <div className="modal-metrics">
              <div className="modal-metric">
                <span className="metric-label">Max Drawdown:</span>
                <span className="metric-value negative">{formatNumber(result.max_drawdown)}%</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Sharpe Ratio:</span>
                <span className="metric-value">{formatNumber(result.sharpe_ratio)}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Volatility:</span>
                <span className="metric-value">{formatNumber(result.volatility)}%</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">VaR (95%):</span>
                <span className="metric-value">{formatNumber(result.var_95)}%</span>
              </div>
            </div>
          </div>

          {/* Financial Metrics */}
          <div className="modal-section">
            <h3>Financial Performance</h3>
            <div className="modal-metrics">
              <div className="modal-metric">
                <span className="metric-label">Final Equity:</span>
                <span className="metric-value">{formatCurrency(result.final_equity)}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Total PnL:</span>
                <span className={`metric-value ${result.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(result.total_pnl)}
                </span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Average Trade:</span>
                <span className="metric-value">{formatCurrency(result.avg_trade_pnl)}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Best Trade:</span>
                <span className="metric-value positive">{formatCurrency(result.best_trade)}</span>
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="modal-section">
            <h3>Trade Statistics</h3>
            <div className="modal-metrics">
              <div className="modal-metric">
                <span className="metric-label">Winning Trades:</span>
                <span className="metric-value">{result.winning_trades}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Losing Trades:</span>
                <span className="metric-value">{result.losing_trades}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Average Win:</span>
                <span className="metric-value positive">{formatCurrency(result.avg_win)}</span>
              </div>
              <div className="modal-metric">
                <span className="metric-label">Average Loss:</span>
                <span className="metric-value negative">{formatCurrency(result.avg_loss)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortfolioResults;